<template>
  <div
    class="kb-mode-fields"
    :class="{
      'kb-mode-fields--dark': dark,
      'kb-mode-fields--upload': mode === 'upload',
      'kb-mode-fields--manual': mode === 'manual',
    }"
  >
    <v-btn-toggle
      v-if="!hideToggle"
      :model-value="mode"
      mandatory
      divided
      color="primary"
      density="compact"
      class="kb-mode-toggle"
      @update:model-value="$emit('update:mode', $event)"
    >
      <v-btn value="manual" size="small" class="text-none">Manual</v-btn>
      <v-btn value="upload" size="small" class="text-none">Upload</v-btn>
    </v-btn-toggle>

    <v-textarea
      v-if="mode === 'manual'"
      :model-value="text"
      label="Knowledge base"
      variant="outlined"
      density="compact"
      hide-details="auto"
      :rules="manualRules"
      rows="18"
      no-resize
      rounded="lg"
      class="kb-mode-fields__content kb-mode-fields__manual-text"
      @update:model-value="$emit('update:text', $event)"
    />

    <div v-else class="kb-mode-fields__content kb-mode-fields__upload">
      <div class="kb-upload-layout">
        <aside class="kb-upload-layout__files">
          <v-file-upload
            :model-value="file"
            multiple
            accept=".txt,.pdf,application/pdf,text/plain"
            :rules="uploadRules"
            density="compact"
            hide-details="auto"
            hide-divider
            icon="mdi-cloud-upload-outline"
            clearable
            :title="hasExistingKeys ? 'Add more files' : 'Drag & drop or upload'"
            subtitle=".txt or .pdf — multiple files allowed."
            browse-text="Browse files"
            class="kb-file-upload"
            @update:model-value="onFileUploadChange"
          >
            <template #item />
          </v-file-upload>
          <div
            v-if="fileEntries.length"
            class="kb-file-list kb-file-list--side rounded-lg"
          >
            <p class="kb-file-list__heading px-2 pt-2 mb-1">
              {{ fileListHeading }}
              <span class="kb-file-list__count">({{ fileEntries.length }})</span>
            </p>
            <ul class="kb-file-list__items mb-0 px-2 pb-2">
              <li
                v-for="entry in fileEntries"
                :key="entry.id"
                class="kb-file-list__row"
              >
                <button
                  type="button"
                  class="kb-file-list__item"
                  :class="{
                    'kb-file-list__item--active': selectedFileId === entry.id,
                  }"
                  @click="selectFileEntry(entry)"
                >
                  <span
                    class="kb-file-list__icon-wrap"
                    :style="fileIconWrapStyle(entry.kind)"
                  >
                    <v-icon
                      :icon="fileIcon(entry.kind)"
                      size="16"
                      :color="fileIconColor(entry.kind)"
                    />
                  </span>
                  <span class="kb-file-list__label text-truncate">{{
                    entry.label
                  }}</span>
                  <v-icon
                    v-if="
                      selectedFileId === entry.id && selectedPreview?.loading
                    "
                    icon="mdi-loading"
                    size="14"
                    class="kb-file-list__spinner"
                  />
                </button>
                <v-btn
                  icon="mdi-close"
                  size="x-small"
                  variant="text"
                  color="error"
                  aria-label="Remove file"
                  class="kb-file-list__remove"
                  @click.stop="removeFileEntry(entry)"
                />
              </li>
            </ul>
          </div>
          <div
            v-else
            class="kb-file-list kb-file-list--side kb-file-list--empty rounded-lg"
          >
            <p class="kb-file-list__empty pa-3 mb-0 text-center">
              No files yet. Upload a file to get started.
            </p>
          </div>
        </aside>

        <section class="kb-upload-layout__main">
          <div
            v-if="selectedPreview"
            class="kb-existing-preview kb-existing-preview--panel rounded-lg"
          >
            <div
              class="d-flex align-center justify-space-between px-2 py-1 kb-existing-preview__head"
            >
              <div class="d-flex align-center ga-2 min-w-0">
                <span
                  class="kb-file-list__icon-wrap kb-file-list__icon-wrap--sm flex-shrink-0"
                  :style="fileIconWrapStyle(selectedPreview.kind)"
                >
                  <v-icon
                    :icon="fileIcon(selectedPreview.kind)"
                    size="14"
                    :color="fileIconColor(selectedPreview.kind)"
                  />
                </span>
                <span class="kb-existing-preview__title text-truncate">
                  {{ selectedPreview.label }}
                </span>
              </div>
              <div class="d-flex align-center ga-1 flex-shrink-0">
                <v-btn
                  v-if="
                    selectedPreview.kind === 'pdf' &&
                    selectedPreview.url &&
                    !selectedPreview.loading
                  "
                  size="x-small"
                  variant="text"
                  class="text-none"
                  @click="openPreviewExternally"
                >
                  Open
                </v-btn>
                <v-btn
                  size="x-small"
                  variant="text"
                  icon="mdi-close"
                  aria-label="Close preview"
                  @click="closePreview"
                />
              </div>
            </div>

            <div class="kb-existing-preview__body">
              <div
                v-if="selectedPreview.loading"
                class="kb-existing-preview--loading d-flex align-center justify-center"
              >
                <v-progress-circular
                  indeterminate
                  color="primary"
                  size="28"
                  width="3"
                />
              </div>

              <embed
                v-else-if="selectedPreview.kind === 'pdf' && selectedPreview.url"
                :src="selectedPreview.url"
                type="application/pdf"
                class="kb-existing-preview__pdf"
              />

              <v-textarea
                v-else-if="
                  selectedPreview.kind === 'text' && selectedPreview.text != null
                "
                :model-value="selectedPreview.text"
                variant="outlined"
                density="compact"
                hide-details
                no-resize
                class="kb-existing-preview__text"
                @update:model-value="onPreviewTextEdit"
              />

              <p v-else class="kb-existing-preview__empty-msg px-2 py-2 mb-0">
                Preview unavailable
              </p>
            </div>
          </div>

          <div
            v-else
            class="kb-existing-preview kb-existing-preview--empty rounded-lg d-flex align-center justify-center"
          >
            <p class="kb-existing-preview__empty-msg pa-3 mb-0 text-center">
              Select a file from the list to preview
            </p>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import { normalizeKnowledgeBaseFiles } from '@/mixins/Organization/UploadKnowledgeBase.js'
import knowledgeBasePreviewMixin from '@/mixins/Organization/knowledgeBasePreviewMixin.js'
import {
  filterKnowledgeBasePreviewKeys,
  normalizeKnowledgeBaseS3Keys,
} from '@/mixins/Organization/OrganizationStorage.js'
import {
  getKnowledgeBaseFileVisual,
  knowledgeBaseFileIconStyle,
} from '@/utils/knowledgeBaseFiles.js'

export default {
  name: 'KnowledgeBaseModeFields',
  mixins: [knowledgeBasePreviewMixin],
  props: {
    mode: { type: String, default: 'manual' },
    text: { type: String, default: '' },
    file: { type: [Array, File, Object], default: null },
    manualRules: { type: Array, default: () => [] },
    uploadRules: { type: Array, default: () => [] },
    existingS3Keys: { type: Array, default: () => [] },
    replaceS3Keys: { type: Object, default: () => ({}) },
    orgId: { type: String, default: '' },
    dark: { type: Boolean, default: false },
    hideToggle: { type: Boolean, default: false },
  },
  emits: [
    'update:mode',
    'update:text',
    'update:file',
    'update:existingS3Keys',
    'update:replaceS3Keys',
  ],
  computed: {
    hasSelectedFile() {
      return normalizeKnowledgeBaseFiles(this.file).length > 0
    },
    hasExistingKeys() {
      return filterKnowledgeBasePreviewKeys(this.existingS3Keys).length > 0
    },
    fileListHeading() {
      if (this.hasExistingKeys && this.hasSelectedFile) return 'All files'
      if (this.hasSelectedFile) return 'Selected files'
      return 'Current files'
    },
    fileEntries() {
      const existing = this.buildExistingFileEntries(
        this.existingS3Keys,
        this.orgId,
      )
      const local = this.buildLocalFileEntries(this.file)
      return [...existing, ...local]
    },
  },
  watch: {
    fileEntries: {
      handler(entries) {
        if (
          this.selectedFileId &&
          !entries.some((entry) => entry.id === this.selectedFileId)
        ) {
          this.closePreview()
        }

        if (this.mode !== 'upload') return

        if (
          this.selectedFileId &&
          entries.some((entry) => entry.id === this.selectedFileId)
        ) {
          return
        }

        this.autoSelectFirstFileEntry(entries)
      },
    },
    mode(mode) {
      if (mode !== 'upload') {
        this.closePreview()
        return
      }
      this.$nextTick(() => this.autoSelectFirstFileEntry(this.fileEntries))
    },
    existingS3Keys() {
      if (this.mode !== 'upload') return
      if (
        this.selectedFileId &&
        this.fileEntries.some((entry) => entry.id === this.selectedFileId)
      ) {
        return
      }
      this.$nextTick(() => this.autoSelectFirstFileEntry(this.fileEntries))
    },
  },
  methods: {
    fileIcon(kind) {
      return getKnowledgeBaseFileVisual(kind).icon
    },
    fileIconColor(kind) {
      return getKnowledgeBaseFileVisual(kind).color
    },
    fileIconWrapStyle(kind) {
      return knowledgeBaseFileIconStyle(kind)
    },
    setReplaceS3Key(fileName, s3Key) {
      const name = String(fileName || '').trim()
      const key = String(s3Key || '').trim()
      if (!name || !key) return
      this.$emit('update:replaceS3Keys', {
        ...this.replaceS3Keys,
        [name]: key,
      })
    },
    clearReplaceS3Key(fileName) {
      const name = String(fileName || '').trim()
      if (!name || !this.replaceS3Keys[name]) return
      const next = { ...this.replaceS3Keys }
      delete next[name]
      this.$emit('update:replaceS3Keys', next)
    },
    onFileUploadChange(value) {
      const incoming = normalizeKnowledgeBaseFiles(value)
      const current = normalizeKnowledgeBaseFiles(this.file)
      const merged = this.mergeKnowledgeBaseFileSelection(current, incoming)

      this.$emit('update:file', merged.length ? merged : null)
      if (!merged.length) {
        this.closePreview()
        return
      }
      this.$nextTick(() => {
        const lastEntry = this.fileEntries[this.fileEntries.length - 1]
        if (lastEntry && this.selectedFileId !== lastEntry.id) {
          this.selectFileEntry(lastEntry)
        }
      })
    },
    mergeKnowledgeBaseFileSelection(current = [], incoming = []) {
      const merged = [...current]

      for (const file of incoming) {
        const duplicateIndex = merged.findIndex(
          (item) =>
            item.name === file.name &&
            item.size === file.size &&
            item.lastModified === file.lastModified,
        )

        if (duplicateIndex === -1) {
          merged.push(file)
        } else {
          merged[duplicateIndex] = file
        }
      }

      return merged
    },
    removeFileEntry(entry) {
      if (this.selectedFileId === entry.id) {
        this.closePreview()
      }

      if (entry.source === 'local') {
        this.clearReplaceS3Key(entry.file?.name)
        const files = normalizeKnowledgeBaseFiles(this.file)
        files.splice(entry.index, 1)
        this.$emit('update:file', files.length ? files : null)
        return
      }

      const metadataKey = `${entry.key}.metadata.json`
      const nextKeys = normalizeKnowledgeBaseS3Keys(this.existingS3Keys).filter(
        (key) => key !== entry.key && key !== metadataKey,
      )
      this.$emit('update:existingS3Keys', nextKeys)
    },
    onPreviewTextEdit(text) {
      const entry = this.fileEntries.find((e) => e.id === this.selectedFileId)
      if (!entry || entry.kind !== 'text') return

      const fileName =
        entry.source === 'local'
          ? entry.file.name
          : /\.txt$/i.test(entry.label)
            ? entry.label
            : `${entry.label}.txt`

      const stableModified =
        entry.source === 'local'
          ? entry.file.lastModified
          : this.selectedPreview.stagedModified || Date.now()

      let files = [...normalizeKnowledgeBaseFiles(this.file)]
      const editIndex =
        entry.source === 'local'
          ? entry.index
          : files.findIndex((file) => file.name === fileName)

      const nextFile = new File([text], fileName, {
        type: 'text/plain',
        lastModified: stableModified,
      })

      if (editIndex >= 0) {
        files[editIndex] = nextFile
      } else {
        files.push(nextFile)
      }

      const localIndex = editIndex >= 0 ? editIndex : files.length - 1
      const localId = `local-${localIndex}-${fileName}`

      const replaceS3Key =
        entry.source === 'existing'
          ? entry.key
          : this.replaceS3Keys[fileName] || this.selectedPreview?.replaceS3Key || ''

      if (entry.source === 'existing') {
        this.selectedFileId = localId
        this.setReplaceS3Key(fileName, entry.key)
      }

      this.selectedPreview = {
        ...this.selectedPreview,
        id: localId,
        source: 'local',
        label: fileName,
        text,
        stagedModified: stableModified,
        replaceS3Key,
      }

      if (entry.source === 'existing') {
        const metadataKey = `${entry.key}.metadata.json`
        const nextKeys = normalizeKnowledgeBaseS3Keys(this.existingS3Keys).filter(
          (key) => key !== entry.key && key !== metadataKey,
        )
        if (nextKeys.length !== this.existingS3Keys.length) {
          this.$emit('update:existingS3Keys', nextKeys)
        }
      }

      this.$emit('update:file', files.length ? files : null)
    },
  },
}
</script>

<style scoped>
.kb-mode-fields {
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-height: 0;
  height: 100%;
}

.kb-mode-fields--upload {
  flex: 1;
  min-height: 0;
  height: 100%;
}

.kb-mode-fields--manual {
  flex: 1;
  min-height: 0;
}

.kb-mode-fields__manual-text {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.kb-mode-fields__manual-text :deep(.v-input) {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.kb-mode-fields__manual-text :deep(.v-input__control) {
  flex: 1;
  min-height: 0;
}

.kb-mode-fields__manual-text :deep(.v-field) {
  flex: 1;
  min-height: 0;
  height: 100%;
}

.kb-mode-fields__manual-text :deep(.v-field__field) {
  height: 100%;
}

.kb-mode-fields__manual-text :deep(textarea) {
  height: 100% !important;
  min-height: 360px;
  max-height: none !important;
  overflow-y: auto !important;
}

.kb-mode-toggle {
  align-self: flex-start;
  height: 32px;
  flex-shrink: 0;
}

.kb-mode-toggle :deep(.v-btn) {
  min-width: 72px;
  height: 32px !important;
  font-size: 0.75rem;
  letter-spacing: 0;
}

.kb-mode-fields__content {
  width: 100%;
}

.kb-mode-fields__upload {
  flex: 1;
  min-height: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.kb-file-upload {
  flex: 0 0 auto;
  align-self: flex-start;
  width: 100%;
}

.kb-file-upload :deep(.v-file-upload),
.kb-file-upload :deep(.v-input) {
  flex: 0 0 auto !important;
  height: auto !important;
  min-height: 0 !important;
}

.kb-file-upload :deep(.v-file-upload-dropzone) {
  flex: 0 0 auto;
  min-height: 0;
  padding: 10px 14px;
  border-radius: var(--y-radius-md, 12px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 2px;
}

.kb-file-upload :deep(.v-file-upload-divider) {
  display: none !important;
}

.kb-file-upload :deep(.v-file-upload-icon) {
  margin: 0;
  opacity: 0.9;
}

.kb-file-upload :deep(.v-file-upload-icon .v-icon) {
  font-size: 28px;
}

.kb-file-upload :deep(.v-file-upload-title) {
  width: 100%;
  margin: 0;
  text-align: center;
  line-height: 1.35;
  font-size: 0.8125rem;
  font-weight: 600;
}

.kb-file-upload :deep(.v-file-upload-subtitle) {
  width: 100%;
  margin: 0;
  text-align: center;
  line-height: 1.3;
  font-size: 0.75rem;
}

.kb-file-upload :deep(.v-file-upload) {
  gap: 0;
}

.kb-file-upload :deep(.v-btn) {
  margin-top: 4px;
}

.kb-file-upload :deep(.v-file-upload-list),
.kb-file-upload :deep(.v-file-upload-item) {
  display: none !important;
}

.kb-upload-layout {
  display: grid;
  grid-template-columns: minmax(200px, 280px) 1fr;
  gap: 16px;
  flex: 1;
  min-height: 0;
  height: 100%;
  align-items: stretch;
}

.kb-upload-layout__files {
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  gap: 6px;
  min-height: 0;
  overflow: hidden;
  align-content: start;
}

.kb-upload-layout__main {
  display: flex;
  flex-direction: column;
  min-height: 0;
  min-width: 0;
  height: 100%;
}

.kb-file-list {
  border: 1px solid var(--y-border, rgba(0, 0, 0, 0.12));
  background: var(--y-surface, #fff);
}

.kb-file-list--side {
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.kb-file-list--side .kb-file-list__items {
  flex: 1 1 auto;
  min-height: 0;
  max-height: 100%;
  overflow-y: auto;
}

.kb-file-list--empty {
  flex: 0 0 auto;
}

.kb-file-list__items {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.kb-file-list__row {
  display: flex;
  align-items: center;
  gap: 2px;
}

.kb-file-list__item {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
  padding: 8px 10px;
  border: 1px solid transparent;
  border-radius: 8px;
  background: transparent;
  color: inherit;
  cursor: pointer;
  text-align: left;
  transition: background 0.15s ease, border-color 0.15s ease;
}

.kb-file-list__item:hover {
  background: var(--y-primary-muted, rgba(var(--v-theme-primary), 0.06));
}

.kb-file-list__item--active {
  border-color: rgb(var(--v-theme-primary));
  background: var(--y-primary-muted, rgba(var(--v-theme-primary), 0.08));
}

.kb-file-list__icon-wrap {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  flex-shrink: 0;
}

.kb-file-list__icon-wrap--sm {
  width: 24px;
  height: 24px;
}

.kb-file-list__label {
  flex: 1;
  min-width: 0;
  font-size: 0.8125rem;
}

.kb-file-list__spinner {
  flex-shrink: 0;
  animation: kb-spin 0.8s linear infinite;
}

.kb-file-list__remove {
  flex-shrink: 0;
}

@keyframes kb-spin {
  to {
    transform: rotate(360deg);
  }
}

.kb-existing-preview {
  border: 1px solid var(--y-border, rgba(0, 0, 0, 0.12));
  background: var(--y-surface, #fff);
  overflow: hidden;
}

.kb-existing-preview--panel {
  flex: 1;
  min-height: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.kb-existing-preview__body {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.kb-existing-preview--panel .kb-existing-preview__pdf,
.kb-existing-preview--panel .kb-existing-preview--loading,
.kb-existing-preview--panel .kb-existing-preview__text {
  flex: 1;
  min-height: 0;
  height: 100%;
}

.kb-existing-preview--empty {
  flex: 1;
  min-height: 0;
  height: 100%;
  border-style: dashed;
  background: var(--y-surface, #fff);
}

.kb-existing-preview--loading {
  min-height: 120px;
}

.kb-existing-preview__head {
  flex-shrink: 0;
  border-bottom: 1px solid var(--y-border, rgba(0, 0, 0, 0.12));
}

.kb-existing-preview__pdf {
  display: block;
  width: 100%;
  height: 100%;
  min-height: 0;
  border: 0;
  background: var(--y-surface, #fff);
}

.kb-existing-preview__text {
  flex: 1;
  min-height: 0;
  height: 100%;
  padding: 8px 10px 10px;
  display: flex;
  flex-direction: column;
}

.kb-existing-preview__text :deep(.v-input) {
  flex: 1;
  min-height: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.kb-existing-preview__text :deep(.v-input__control) {
  flex: 1;
  min-height: 0;
  height: 100%;
}

.kb-existing-preview__text :deep(.v-field) {
  font-size: 0.8125rem;
  flex: 1;
  min-height: 0;
  height: 100%;
}

.kb-existing-preview__text :deep(.v-field__field) {
  height: 100%;
  min-height: 0;
}

.kb-existing-preview__text :deep(textarea) {
  height: 100% !important;
  min-height: 0 !important;
  max-height: none !important;
  overflow-y: auto !important;
}

/* Dark theme (portal edit dialog) */
.kb-mode-fields--dark .kb-file-list__heading {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--y-text-secondary);
  margin: 0;
}

.kb-mode-fields--dark .kb-file-list__count {
  font-weight: 500;
  color: var(--y-text-muted);
}

.kb-mode-fields--dark .kb-file-list__empty,
.kb-mode-fields--dark .kb-existing-preview__empty-msg {
  font-size: 0.75rem;
  color: var(--y-text-muted);
  margin: 0;
}

.kb-mode-fields--dark .kb-existing-preview__title {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--y-text);
}

.kb-mode-fields--dark .kb-file-list {
  border-color: var(--y-glass-border);
  background: var(--y-glass-surface);
}

.kb-mode-fields--dark .kb-file-list__item {
  color: var(--y-text);
}

.kb-mode-fields--dark .kb-file-list__item:hover {
  background: rgba(99, 102, 241, 0.12);
}

.kb-mode-fields--dark .kb-file-list__item--active {
  border-color: #6366f1;
  background: rgba(99, 102, 241, 0.18);
}

.kb-mode-fields--dark .kb-file-list__label {
  color: var(--y-text);
}

.kb-mode-fields--dark .kb-existing-preview {
  border-color: var(--y-glass-border);
  background: var(--y-glass-surface);
}

.kb-mode-fields--dark .kb-existing-preview--empty {
  background: var(--y-panel-bg);
  border-color: var(--y-glass-border);
}

.kb-mode-fields--dark .kb-existing-preview__head {
  border-bottom-color: var(--y-glass-border);
}

.kb-mode-fields--dark .kb-existing-preview__pdf {
  background: rgba(15, 23, 42, 0.6);
}

.kb-mode-fields--dark .kb-file-upload :deep(.v-file-upload-dropzone) {
  border-color: var(--y-glass-border);
  background: var(--y-glass-surface);
  color: var(--y-text-secondary);
}

.kb-mode-fields--dark .kb-file-upload :deep(.v-file-upload-title) {
  color: var(--y-text);
}

.kb-mode-fields--dark .kb-file-upload :deep(.v-file-upload-subtitle) {
  color: var(--y-text-muted);
}

.kb-mode-fields--dark .kb-mode-toggle {
  border: 1px solid var(--y-glass-border);
  border-radius: 10px;
  overflow: hidden;
  background: var(--y-glass-surface);
}

.kb-mode-fields--dark .kb-mode-toggle :deep(.v-btn) {
  color: var(--y-text-secondary) !important;
  background: transparent !important;
  opacity: 1 !important;
}

.kb-mode-fields--dark .kb-mode-toggle :deep(.v-btn .v-btn__content) {
  color: inherit !important;
}

.kb-mode-fields--dark .kb-mode-toggle :deep(.v-btn--active),
.kb-mode-fields--dark .kb-mode-toggle :deep(.v-btn--selected) {
  color: var(--y-text) !important;
  background: rgba(99, 102, 241, 0.4) !important;
}

.kb-mode-fields--dark .kb-mode-toggle :deep(.v-btn:hover:not(.v-btn--active)) {
  background: rgba(255, 255, 255, 0.08) !important;
  color: var(--y-text) !important;
}

.kb-mode-fields--dark .kb-existing-preview__text :deep(.v-field) {
  background: var(--y-glass-surface);
  color: var(--y-text);
}

.kb-mode-fields--dark .kb-existing-preview__text :deep(textarea) {
  color: var(--y-text);
}

@media (max-width: 960px) {
  .kb-upload-layout {
    grid-template-columns: 1fr;
    grid-template-rows: auto 1fr;
  }

  .kb-file-list--side {
    max-height: 200px;
  }
}
</style>
